// Email Notification Service
// Handles all email notifications for job applications and requests

import emailjs from 'emailjs-com';
import { db } from '../config/firebase';
import { ref, set, get } from 'firebase/database';

// Initialize EmailJS
const initializeEmailJS = () => {
  const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
  const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
  const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;

  if (!publicKey || publicKey === 'your_public_key') {
    console.warn('EmailJS public key not configured. Email notifications will not be sent.');
    return false;
  }

  if (!serviceId || serviceId === 'your_service_id') {
    console.warn('EmailJS service ID not configured. Email notifications will not be sent.');
    return false;
  }

  if (!templateId || templateId === 'your_template_id') {
    console.warn('EmailJS template ID not configured. Email notifications will not be sent.');
    return false;
  }

  if (!window.emailJSInitialized) {
    emailjs.init(publicKey);
    window.emailJSInitialized = true;
  }

  return true;
};

// Status message templates
const STATUS_MESSAGES = {
  APPLICATION: {
    underReview: 'Your job application is currently under review by our team. We will notify you soon.',
    accepted: 'Congratulations! Your application has been accepted. Please proceed to the nearest PEIS office for further update. Your queue number: {queueNumber}',
    rejected: 'Unfortunately, your application has been rejected. Reason: {reason}. We encourage you to apply for other positions.'
  },
  JOB_REQUEST: {
    underReview: 'Your job request is currently under review by our team. We will get back to you shortly.',
    accepted: 'Your job request has been accepted! Please proceed to your nearest PEIS office for briefing. Your queue number: {queueNumber}',
    rejected: 'Your job request has been rejected. Reason: {reason}. Please feel free to submit another request.'
  }
};

// Core email sender — uses a single EmailJS template
const sendEmailNotification = async (userEmail, subject, message, type, status) => {
  try {
    const isInitialized = initializeEmailJS();

    // If EmailJS is not configured, log and store in Firebase only
    if (!isInitialized) {
      console.log('EmailJS not configured. Storing notification in Firebase only:', {
        to: userEmail,
        subject,
        message,
        type,
        status
      });

      const emailLogRef = ref(
        db,
        `emailLogs/${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      );

      await set(emailLogRef, {
        userEmail: userEmail || 'unknown',
        subject: subject || 'unknown',
        message: message || 'unknown',
        type: type || 'unknown',
        status: status || 'unknown',
        sentAt: new Date().toISOString(),
        emailJSStatus: 'skipped_not_configured'
      });

      return true;
    }

    const templateParams = {
      to_email: userEmail,
      subject: subject,
      message: message,
      type: type,
      status: status
    };

    // Send using the single template from .env
    const response = await emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
      templateParams
    );

    console.log('Email sent successfully:', {
      to: userEmail,
      subject,
      status: response.status
    });

    // Log to Firebase
    const emailLogRef = ref(
      db,
      `emailLogs/${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    );

    await set(emailLogRef, {
      userEmail: userEmail || 'unknown',
      subject: subject || 'unknown',
      message: message || 'unknown',
      type: type || 'unknown',
      status: status || 'unknown',
      sentAt: new Date().toISOString(),
      emailJSStatus: response.status || 'success'
    });

    return true;
  } catch (error) {
    console.error('Error sending email notification:', error);

    // Log error to Firebase
    const errorLogRef = ref(
      db,
      `emailErrors/${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    );

    let errorMessage = 'Unknown error';
    let errorDetails = {};

    if (error) {
      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error.text) {
        errorMessage = error.text;
      } else if (error.message) {
        errorMessage = error.message;
      }

      if (error.status) errorDetails.status = error.status;
      if (error.responseText) errorDetails.responseText = error.responseText;
    }

    await set(errorLogRef, {
      userEmail: userEmail || 'unknown',
      subject: subject || 'unknown',
      message: message || 'unknown',
      errorMessage,
      errorDetails,
      timestamp: new Date().toISOString()
    });

    throw error;
  }
};

// Build message from template, replacing placeholders
const buildMessage = (type, status, reason, queueNumber) => {
  let message = STATUS_MESSAGES[type]?.[status] || '';

  if (reason) message = message.replace('{reason}', reason);
  if (queueNumber) message = message.replace('{queueNumber}', queueNumber);

  return message;
};

// Notify for job application status change
export const sendApplicationStatusNotification = async (
  userId,
  userEmail,
  jobTitle,
  status,
  reason = null,
  queueNumber = null
) => {
  try {
    // Check if user exists before proceeding
    const userSnapshot = await get(ref(db, `users/${userId}`));
    if (!userSnapshot.exists()) {
      console.warn(`User ${userId} not found. Skipping application notification.`);
      return;
    }

    const message = buildMessage('APPLICATION', status, reason, queueNumber);
    const subject = `Job Application ${status.charAt(0).toUpperCase() + status.slice(1)} - ${jobTitle}`;

    // Save notification to user's profile in Firebase
    const notificationRef = ref(db, `users/${userId}/emailNotifications/${Date.now()}`);
    await set(notificationRef, {
      type: 'APPLICATION',
      jobTitle,
      status,
      message,
      reason: reason || null,
      queueNumber: queueNumber || null,
      sentAt: new Date().toISOString(),
      read: false
    });

    // Send email
    await sendEmailNotification(userEmail, subject, message, 'APPLICATION', status);

    console.log('✅ Application notification sent:', { userEmail, status, jobTitle });
  } catch (error) {
    console.error('Error sending application notification:', error);
    throw error;
  }
};

// Notify for job request status change
export const sendJobRequestStatusNotification = async (
  userId,
  userEmail,
  requestTitle,
  status,
  reason = null,
  queueNumber = null
) => {
  try {
    // Check if user exists before proceeding
    const userSnapshot = await get(ref(db, `users/${userId}`));
    if (!userSnapshot.exists()) {
      console.warn(`User ${userId} not found. Skipping job request notification.`);
      return;
    }

    const message = buildMessage('JOB_REQUEST', status, reason, queueNumber);
    const subject = `Job Request ${status.charAt(0).toUpperCase() + status.slice(1)} - ${requestTitle}`;

    // Save notification to user's profile in Firebase
    const notificationRef = ref(db, `users/${userId}/emailNotifications/${Date.now()}`);
    await set(notificationRef, {
      type: 'JOB_REQUEST',
      requestTitle,
      status,
      message,
      reason: reason || null,
      queueNumber: queueNumber || null,
      sentAt: new Date().toISOString(),
      read: false
    });

    // Send email
    await sendEmailNotification(userEmail, subject, message, 'JOB_REQUEST', status);

    console.log('✅ Job request notification sent:', { userEmail, status, requestTitle });
  } catch (error) {
    console.error('Error sending job request notification:', error);
    throw error;
  }
};

// Get a single status message (for preview or display)
export const getStatusMessage = (type, status, options = {}) => {
  return buildMessage(type, status, options.reason, options.queueNumber);
};

// Export all message templates
export const getMessageTemplates = () => ({
  APPLICATION: STATUS_MESSAGES.APPLICATION,
  JOB_REQUEST: STATUS_MESSAGES.JOB_REQUEST
});